/**
 * WebsiteOffer - Component for offering website creation to users without a website
 */

import React, { FC, useState } from 'react';
import { Button, Flex, Text, VStack, Box, Icon } from '@chakra-ui/react';
import { useRealtimeChatContext } from '../hooks/useRealtimeChatContext';

// Globe icon component
const GlobeIcon: FC<{ boxSize?: string }> = ({ boxSize = '24px' }) => (
  <svg
    width={boxSize}
    height={boxSize}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
    <ellipse cx="12" cy="12" rx="4" ry="9" stroke="currentColor" strokeWidth="2" />
    <path d="M3 12H21" stroke="currentColor" strokeWidth="2" />
    <path d="M12 3V21" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const WebsiteOffer: FC = () => {
  const { sendMessage } = useRealtimeChatContext();
  const [selectedOption, setSelectedOption] = useState<'yes' | 'no' | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleYes = async () => {
    setSelectedOption('yes');
    setIsLoading(true);
    sendMessage('Yes, please create a website for me');
  };

  const handleNo = async () => {
    setSelectedOption('no');
    setIsLoading(true);
    sendMessage("No thanks, I don't need a website right now");
  };

  return (
    <Box
      borderRadius="xl"
      bg="linear-gradient(135deg, #f8fafc 0%, #eef2f7 100%)"
      border="1px solid"
      borderColor="gray.200"
      p={6}
      maxW="500px"
      boxShadow="sm"
    >
      <VStack spacing={4} align="stretch">
        <Flex align="center" gap={3}>
          <Box
            p={3}
            borderRadius="full"
            bg="orange.100"
            color="orange.500"
          >
            <GlobeIcon boxSize="28px" />
          </Box>
          <VStack align="start" spacing={0}>
            <Text
              fontWeight="bold"
              fontSize="lg"
              color="gray.800"
              fontFamily="'Noto Sans', 'Open Sans', sans-serif"
            >
              Create Your Website
            </Text>
            <Text
              fontSize="sm"
              color="gray.600"
              fontFamily="'Noto Sans', 'Open Sans', sans-serif"
            >
              Get a professional website in minutes
            </Text>
          </VStack>
        </Flex>

        <Text
          fontSize="sm"
          color="gray.600"
          fontFamily="'Noto Sans', 'Open Sans', sans-serif"
        >
          We can create a beautiful, professional website for your business with AI. 
          It&apos;s free and takes just a few minutes!
        </Text>

        <Flex gap={3} flexWrap="wrap" pt={2}>
          <Button
            flex="1"
            minW="140px"
            bg={selectedOption === 'yes' ? 'orange.500' : 'orange.base'}
            color="white"
            _hover={{ bg: 'orange.600' }}
            size="lg"
            onClick={handleYes}
            isLoading={selectedOption === 'yes' && isLoading}
            loadingText="Creating..."
            isDisabled={selectedOption !== null}
            borderRadius="xl"
            fontFamily="'Noto Sans', 'Open Sans', sans-serif"
            fontWeight="semibold"
            leftIcon={selectedOption === 'yes' ? <Text>✓</Text> : undefined}
          >
            {selectedOption === 'yes' ? 'Selected' : "Yes, let's do it!"}
          </Button>
          <Button
            flex="1"
            minW="140px"
            variant="outline"
            borderColor="gray.300"
            color="gray.600"
            _hover={{ bg: 'gray.50', borderColor: 'gray.400' }}
            size="lg"
            onClick={handleNo}
            isLoading={selectedOption === 'no' && isLoading}
            loadingText="Skipping..."
            isDisabled={selectedOption !== null}
            borderRadius="xl"
            fontFamily="'Noto Sans', 'Open Sans', sans-serif"
            fontWeight="medium"
          >
            No thanks
          </Button>
        </Flex>
      </VStack>
    </Box>
  );
};

export default WebsiteOffer;

